/**
 * MediaStore
 *
 * The media store is not saved.
 * It can be filled with all loaded media data.
 * On save, all new media is saved seperately.
 */

import { mapKeys } from "lodash-es";
import { acceptHMRUpdate, defineStore } from "pinia";
import { v4 as uuid } from "uuid";
import * as API from "@/api/API";
import { getUserDB } from "@/api/DB";
import envOptions from "@/helpers/Env";
import { useUser } from "@/api/user.store";
import { dataURLToBlob } from "@/helpers/utility/ReadFile";

interface MediaItem {
  /**
   * The unique ID of the media item.
   */
  uuid: string;

  /**
   * The data of the media entry
   */
  data: string;

  /**
   * Flag if the media was added during the open session.
   * Only *new* media will be uploaded on save.
   */
  isNew?: boolean;
}
interface LocalMediaItem {
  uuid: string;
  data: Blob;
  isNew?: boolean;
  models: (string | number)[];
  uploaded: boolean;
}

export const useMediaStore = defineStore("model/media", {
  state: () => ({
    /**
     * All media items, ordered by their uuid.
     */
    items: {} as { [uuid: string]: MediaItem }
  }),

  getters: {
    asArray(state) {
      return Object.values(state.items);
    },

    /**
     * Return the data of all media entries in a format that can
     * be inserted in image src tags.
     */
    data(state) {
      const entries: { [uuid: string]: string | null } = {};
      for (const uuid in state.items) {
        const item = state.items[uuid];
        const data = item?.data as string | Blob;

        if (typeof data === "string") {
          entries[uuid] = data;
        } else if (data instanceof Blob) {
          entries[uuid] = URL.createObjectURL(data);
        } else {
          entries[uuid] = null;
        }
      }
      return entries;
    },

    uuids(state) {
      return Object.keys(state.items);
    },

    /**
     * The uuids of the media entries that were not uploaded yet
     */
    newMediaUUIDs(): string[] {
      return this.asArray
        .filter((mediaEntry) => mediaEntry.isNew && mediaEntry.data != null)
        .map((item) => item.uuid);
    },

    /**
     * @returns all the media items, formatted as blobs,
     * for saving in a local or online database.
     */
    saveableEntries(): { uuid: string; data: Blob }[] {
      return this.asArray.map((item) => {
        let data: string | Blob = item.data;
        if (typeof data === "string") {
          data = dataURLToBlob(data);
        }
        // Any non-findable image will be replaced with a not-found image:
        if (data == null) {
          const notFoundImageSVGDataUrl = `data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIyNCIgaGVpZ2h0PSIyNCIgdmlld0JveD0iMCAwIDI0IDI0IiBmaWxsPSJub25lIiBzdHJva2U9ImN1cnJlbnRDb2xvciIgc3Ryb2tlLXdpZHRoPSIyIiBzdHJva2UtbGluZWNhcD0icm91bmQiIHN0cm9rZS1saW5lam9pbj0icm91bmQiIGNsYXNzPSJsdWNpZGUgbHVjaWRlLXgiPjxwYXRoIGQ9Ik0xOCA2IDYgMTgiLz48cGF0aCBkPSJtNiA2IDEyIDEyIi8+PC9zdmc+`;
          data = dataURLToBlob(notFoundImageSVGDataUrl);
        }
        return {
          uuid: item.uuid,
          data: data
        };
      });
    }
  },

  actions: {
    /**
     *
     */
    create(newItemOptions: { data: string }) {
      let data = newItemOptions.data;
      const newItem: MediaItem = {
        uuid: uuid(),
        data: data as string,
        isNew: true
      };
      this.add(newItem);
      return newItem;
    },
    /**
     *
     */
    add(item: MediaItem) {
      if (typeof item?.uuid != "string") {
        throw new Error("uuid of item should be string.");
      }
      this.items[item.uuid] = {
        uuid: item.uuid,
        data: item.data,
        isNew: item?.isNew ?? false
      };
      return this.items[item.uuid];
    },

    /**
     * Change the uuid of a media item,
     * marking that item as new.
     */
    recreate(oldUUID: string) {
      const item = this.items[oldUUID];
      if (item == null) {
        throw new Error("Media item not found.");
      }
      delete this.items[oldUUID];
      return this.create(item);
    },

    duplicate(oldUUID: string) {
      const item = this.items[oldUUID];
      if (item == null) {
        throw new Error("Media item not found.");
      }
      const newItem = this.create(item);
      return newItem;
    },

    delete({ uuid }: { uuid: string }) {
      delete this.items[uuid];
    },

    /**
     * Set all media flagged as new to not.
     */
    removeNewFlags(uuids: string[]) {
      for (const id of uuids) {
        this.items[id].isNew = false;
      }
    },

    /**
     * Returns only the entries that have not been uploaded yet.
     */
    async getDBEntriesToUpload(uuids: string[]) {
      const db = getMediaDB();
      return db
        .where("uuid")
        .anyOf(uuids)
        .and((entry) => entry.uploaded == false)
        .toArray();
    },

    /**
     * Removes any amount of entries from the list of new media uuids.
     */
    markMediaAsUploaded(uuids: string[]) {
      const db = getMediaDB();
      return db
        .where("uuid")
        .anyOf(uuids)
        .modify((item) => (item.uploaded = true));
    }
  }
});

export const cloudMediaAPI = {
  /**
   * Load all media files linked to a model version
   * @param loadedMediaList all media entries to load
   */
  async loadModelMedia(
    loadedMediaList: { uuid: string; file: string }[] = [],
    requestOptions: API.RequestOptions = {}
  ) {
    const imageRequests = loadedMediaList.map(async (entry) => {
      const imageURL = entry.file;

      const imageData = await API.get<Blob>(imageURL, {
        responseType: "blob",
        sendAuthToken: false,
        progressTracker: requestOptions?.progressTracker
      });

      return {
        uuid: entry.uuid,
        data: imageData,
        uploaded: true // media files returned from the server are ALWAYS uploaded.
      };
    });

    // Make sure ALL entries are retrieved.
    // If any entry does not make it, we fail.
    // This makes sure that we don't get incomplete models.
    const entries = await Promise.all(imageRequests);

    return entries;
  },

  put(item: { data?: Blob }) {
    return API.put<Blob>("/v1/media", {
      data: item
    });
  }
};
export const localMediaAPI = {
  /**
   *
   * @param loadedMediaList
   * @param modelId
   */
  async loadModelMedia(
    loadedMediaList: { uuid: string }[],
    modelId: number | string
  ) {
    const uuids = loadedMediaList.map((entry) => entry.uuid);

    // get media
    const entries = await getMediaDB()
      .where("models")
      .equals(modelId)
      .and((entry) => uuids.includes(entry.uuid))
      .toArray();

    return entries.filter((entry) => entry != null);
  },
  put(item: LocalMediaItem) {
    if (!item.data) return item;
    if (!item.uploaded) item.uploaded = false;
    const db = getMediaDB();
    return db.put(item);
  },

  /**
   * Save new media entries OR add model references to
   * existing media entries, if the media exists already.
   * If there is any media referenced by the model that is
   * not in the new mediaEntries list, REMOVE those references.
   */
  async saveForModel(
    mediaEntries: { uuid: string; data: Blob }[],
    { model, uploaded }: { model: string | number; uploaded?: boolean }
  ) {
    const entriesByUUIDs = mapKeys(mediaEntries, (entry) => entry.uuid);
    const uuids = Object.keys(entriesByUUIDs);
    const db = getMediaDB();

    if (model == null) {
      throw new Error("Can't save media entries for undefined model.");
    }

    // get the media items that are already connected to the model
    const alreadyConnectedEntries = await db
      .where("models")
      .equals(model)
      .toArray();
    const alreadyConnectedUUIDs = alreadyConnectedEntries.map((i) => i.uuid);

    const notConnectedUUIDs = uuids.filter(
      (id) => !alreadyConnectedUUIDs.includes(id)
    );

    // connect to all media entries that exist already,
    // but are not connected to the model:
    const existingUUIDS: string[] = [];
    await db
      .where("uuid")
      .anyOf(notConnectedUUIDs)
      .modify((entry) => {
        // Make a simple check to test if we're actually dealing
        // with the same image:
        // (not foolproof, but good enough)
        const newMediaEntry = entriesByUUIDs[entry.uuid];
        if (entry.data.size != newMediaEntry.data.size) return;
        if (entry.data.type != newMediaEntry.data.type) return;

        if (!entry.models.includes(model)) {
          entry.models = entry.models.concat([model]);
        }

        existingUUIDS.push(entry.uuid);
      });

    // Create all media entries do not exist in
    // the database, but should be connected to
    // the model
    const newUUIDs = notConnectedUUIDs.filter(
      (uuid) => !existingUUIDS.includes(uuid)
    );
    const newMediaSaves = newUUIDs.map((newMediaUUID) => {
      const mediaItem = entriesByUUIDs[newMediaUUID];
      console.log("create media item", newMediaUUID, mediaItem);
      return localMediaAPI.put({
        data: mediaItem.data,
        uuid: mediaItem.uuid,
        models: [model],
        uploaded: !!uploaded
      });
    });

    // Remove connections to the model that should
    // not be there anymore:
    const entriesToRemove = alreadyConnectedUUIDs.filter(
      (id) => !uuids.includes(id)
    );
    localMediaAPI.removeMediaOfModel(model, entriesToRemove);

    await Promise.all(newMediaSaves);

    return true;
  },

  /**
   *
   */
  replaceModelId(oldModelId: string | number, newModelId: string | number) {
    return getMediaDB()
      .where("models")
      .equals(oldModelId)
      .modify((item) => {
        const index = item.models.indexOf(oldModelId);
        item.models[index] = newModelId;
      });
  },

  /**
   * Copy all the media references of one model to a new one.
   */
  copyModelMedia(oldModelId: string | number, newModelId: string | number) {
    return getMediaDB()
      .where("models")
      .equals(oldModelId)
      .modify((item) => {
        if (!item.models.includes(newModelId)) {
          item.models.push(newModelId);
        }
      });
  },

  /**
   * Remove a modelId from media items.
   * If no uuids are passed, all model
   * @param {(string|number)} modelId
   * @param {string[]?} uuids
   * @returns
   */
  removeMediaOfModel(modelId: number | string, uuids?: string[]) {
    return (
      getMediaDB()
        .where("models")
        .equals(modelId)
        // filter by uuids if uuids were passed:
        .and((item) => (uuids ? uuids.includes(item.uuid) : true))
        .modify((item, ref) => {
          const _ref = ref as { value?: LocalMediaItem };

          const index = item.models.indexOf(modelId);
          item.models.splice(index, 1);

          if (item.models.length == 0) {
            delete _ref.value;
          }
        })
    );
  }
};

export const mediaAPI = envOptions.saveLocally ? localMediaAPI : cloudMediaAPI;

function getMediaDB() {
  const { user } = useUser();
  if (user.value == null) {
    throw new Error("No user loaded.");
  }
  const db = getUserDB(user.value.id);
  return db.media;
}

if (import.meta.hot) {
  import.meta.hot.accept(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    acceptHMRUpdate(useMediaStore as any, import.meta.hot)
  );
}
